import { useGoogleLogin } from '@react-oauth/google'
import { getLoadingStatus } from '@flowwai-frontend/shared/api-common'
import { APIStatus } from '@flowwai-frontend/shared/api-common'

import useAuthorizeGoogleUser from '../apis/AuthorizeGoogleUser/useAuthorizeGoogleUser'
import useRevokeUserGoogleAuthorization from '../apis/RevokeUserGoogleAuthorization/useRevokeUserGoogleAuthorization'
import { GOOGLE_CALENDAR_API_AUTH_SCOPE } from '../constants/GoogleAuthConstants'
import { UserGoogleCalenderAuthConsentStatusType } from '../types/googleAuthTypes'
import { showCRMFailureToastWithGivenMessage } from '../utils/ToastUtil'

interface ReturnType {
   connectToGoogleCalendar: () => void
   disconnectFromGoogleCalendar: () => void
   apiStatus: APIStatus
}

interface ArgsType {
   setGoogleAuthStatus: (
      status: UserGoogleCalenderAuthConsentStatusType | null
   ) => void
}

export const useGoogleCalendarAuthorization = (args: ArgsType): ReturnType => {
   const authorizeGoogleUser = useAuthorizeGoogleUser()
   const revokeGoogleUserAuth = useRevokeUserGoogleAuthorization()

   const onConnectToGoogleCalendarSuccess = (
      authorizationCode: string
   ): void => {
      authorizeGoogleUser.triggerAPI({
         authorizationCode,
         onSuccess: () => {
            args.setGoogleAuthStatus('AUTHORIZATION_GRANTED')
         },
         onFailure(data) {
            if (data.error)
               showCRMFailureToastWithGivenMessage(data.error.message)
         }
      })
   }

   const connectToGoogleCalendar = useGoogleLogin({
      onSuccess: codeResponse =>
         onConnectToGoogleCalendarSuccess(codeResponse.code),
      flow: 'auth-code',
      scope: GOOGLE_CALENDAR_API_AUTH_SCOPE,
      overrideScope: true
   })

   const disconnectFromGoogleCalendar = (): void => {
      revokeGoogleUserAuth.triggerAPI({
         onSuccess: () => {
            args.setGoogleAuthStatus('AUTHORIZATION_NOT_GRANTED')
         },
         onFailure(data) {
            if (data.error)
               showCRMFailureToastWithGivenMessage(data.error.message)
         }
      })
   }

   const apiStatus = getLoadingStatus(
      authorizeGoogleUser.apiStatus,
      revokeGoogleUserAuth.apiStatus
   )

   return {
      connectToGoogleCalendar,
      disconnectFromGoogleCalendar,
      apiStatus
   }
}
